import React from "react";
import { gbp } from "../lib/format";
import { ArrowDown, Landmark, Wrench, ShieldCheck, Coins } from "lucide-react";

const TIER_META = [
  {
    key: "gross_rent",
    label: "Tier 1 — Gross Tenant Rent",
    sub: "Flows into ring-fenced SPV Bank Account",
    icon: Landmark,
    sign: "+",
    color: "#0F172A",
    barTone: "#1E293B",
  },
  {
    key: "ops_deduction",
    label: "Tier 2 — Operational Deductions",
    sub: "Managing Agent Fees & Insurance",
    icon: Wrench,
    sign: "-",
    color: "#B91C1C",
    barTone: "#F1F5F9",
  },
  {
    key: "platform_fee",
    label: "Tier 3 — Brickstok Platform Fee",
    sub: "Technology licensing & compliance monitoring",
    icon: ShieldCheck,
    sign: "-",
    color: "#B45309",
    barTone: "#F1F5F9",
  },
  {
    key: "net_yield",
    label: "Tier 4 — Net Distributable Yield",
    sub: "Converted to Stablecoin for Token Holders",
    icon: Coins,
    sign: "=",
    color: "#0F172A",
    barTone: "#D4AF37",
  },
];

export const LeaseWaterfall = ({ waterfall }) => {
  const max = waterfall.gross_rent;
  const rows = TIER_META.map((tier) => {
    const raw = waterfall[tier.key];
    const width = Math.max(6, Math.round((raw / max) * 100));
    return { ...tier, raw, width };
  });

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-8"
      data-testid="lease-waterfall"
    >
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Cash Waterfall</div>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900">
            How rent flows to token holders
          </h2>
        </div>
        <div className="text-right">
          <div className="overline">Monthly Net</div>
          <div className="font-display text-2xl font-semibold font-mono-num" style={{ color: "#D4AF37" }} data-testid="waterfall-net-value">
            {gbp(waterfall.net_yield)}
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {rows.map((tier, i) => {
          const Icon = tier.icon;
          const isNet = tier.key === "net_yield";
          const isDeduction = tier.sign === "-";
          return (
            <div key={tier.key} data-testid={`waterfall-tier-${tier.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div
                    className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md"
                    style={{ background: isNet ? "#D4AF37" : isDeduction ? "#FEE2E2" : "#F1F5F9" }}
                  >
                    <Icon className="h-4 w-4" style={{ color: isNet ? "#0F172A" : tier.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{tier.label}</div>
                    <div className="text-xs text-slate-500">{tier.sub}</div>
                  </div>
                </div>
                <div
                  className="font-display text-lg font-semibold font-mono-num"
                  style={{ color: isNet ? "#0F172A" : isDeduction ? "#B91C1C" : "#0F172A" }}
                >
                  {tier.sign === "-" ? "-" : tier.sign === "=" ? "" : ""}
                  {gbp(tier.raw)}
                  <span className="ml-1 text-xs font-medium text-slate-400">/mo</span>
                </div>
              </div>

              <div className="mt-2 ml-12">
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full transition-[width] duration-700"
                    style={{
                      width: `${tier.width}%`,
                      background: isNet
                        ? "linear-gradient(90deg,#D4AF37,#B8941F)"
                        : isDeduction
                        ? "repeating-linear-gradient(45deg,#FCA5A5 0 6px,#FEE2E2 6px 12px)"
                        : "#1E293B",
                    }}
                  />
                </div>
              </div>

              {i < rows.length - 1 && (
                <div className="ml-[26px] mt-2 flex items-center gap-2 text-slate-300">
                  <ArrowDown className="h-3.5 w-3.5" />
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LeaseWaterfall;
